import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import { GraphqlQueryContext } from '@interfaces/graphql';
import {
  DnaSearchCategoryQueryResult,
  DnaSearchQueriesSearchCategoryArgs,
  DnaSearchQueriesSearchReportArgs,
  DnaSearchReportQueryResult,
  Query
} from '@interfaces/schema';
import { WebStorageReportOptions } from '@interfaces/web-storage';
import { Session } from '@providers/session/session.service';

/**
 * Report View Search Service.
 *
 * @exports
 * @class ReportViewSearchService
 */
@Injectable()
export class ReportViewSearchService {
  private readonly apollo = inject(Apollo);
  // eslint-disable-next-line kawaī/inject-private-readonly
  protected readonly session = inject(Session);

  /**
   * Get context.
   *
   * @returns {GraphqlQueryContext}
   * @memberof ReportViewSearchService
   */
  get context(): GraphqlQueryContext {
    return {
      contentLanguage: this.language
    };
  }

  /**
   * Get active or selected language.
   *
   * @returns {string}
   * @memberof ReportViewSearchService
   */
  get language(): string | undefined {
    return this.options?.language;
  }

  /**
   * Get option report.
   *
   * @returns {WebStorageReportOptions}
   * @memberof ReportViewSearchService
   */
  get options(): WebStorageReportOptions | null {
    return this.session.retrieve(this.session.keys.reportNourishOptions) ?? {};
  }

  /**
   * Search category query.
   *
   * @param {DnaSearchQueriesSearchCategoryArgs} [variables] Variables.
   * @returns {Observable<DnaSearchCategoryQueryResult>} Observable of search category query result.
   * @memberof ReportViewSearchService
   */
  searchCategory(
    variables?: DnaSearchQueriesSearchCategoryArgs
  ): Observable<DnaSearchCategoryQueryResult> {
    const query = gql`
      query SearchDNACategory($where: DNASearchReportFilter) {
        DNA {
          Search {
            searchCategory(where: $where) {
              count
              results {
                id
                name
                objectImage
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables, context: this.context })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.Search?.searchCategory as DnaSearchCategoryQueryResult
        )
      );
  }

  /**
   * Search report query.
   *
   * @param {DnaSearchQueriesSearchReportArgs} [variables] Variables.
   * @returns {Observable<DnaSearchReportQueryResult>} Observable of search report query result.
   * @memberof ReportViewSearchService
   */
  searchReport(
    variables?: DnaSearchQueriesSearchReportArgs
  ): Observable<DnaSearchReportQueryResult> {
    const query = gql`
      query SearchDNAReport($where: DNASearchReportFilter) {
        DNA {
          Search {
            searchReport(where: $where) {
              count
              results {
                id
                dnaTrait {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables, context: this.context })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.Search?.searchReport as DnaSearchReportQueryResult
        )
      );
  }
}
